/**
 * The Sarva Health brand lockup.
 *
 * Two assets, both trimmed from the supplied PNG so nothing positions itself
 * against invisible padding — the original carried 41px of transparency on the
 * left and 41px on the right, which is enough to make a 24px-tall logo look
 * off-centre in a 48px rail:
 *
 *   `sarva-logo.png`  208×46  the full lockup — mark + "sarva health.ai"
 *   `sarva-mark.png`   56×44  the mark alone, for the collapsed rail
 *
 * ── (H) On a dark ground it is a white knockout, not a plate ────────────────
 *
 * The supplied wordmark is grey and the mark is magenta, both chosen for a
 * light ground. On the sign-in panel's gradient (`rose-1300 → rose-900`) grey
 * fails the 4.5:1 floor at every stop, so it cannot simply be placed there.
 *
 * The first attempt put it on a white plate. That is legible and it looks like
 * a sticker: a hard white rectangle sitting on the brand colour, reading as a
 * patch over the design rather than as part of it.
 *
 * So `tone="light"` serves a knockout — the same artwork with every visible
 * pixel painted white, alpha preserved, so the mark keeps its exact shape. That
 * is the ordinary way a logo goes on a brand-colour background, it needs no
 * container, and it clears the contrast floor by construction because white on
 * `rose-900` is 5.46:1 at the gradient's worst end.
 *
 * `width`/`height` are set on the element so the row reserves its space before
 * the image decodes (doc 27 §8: no layout shift). The rendered size comes from
 * the classes; the attributes only give the browser the aspect ratio.
 */

const ART = {
  dark: { lockup: "/brand/sarva-logo.png", mark: "/brand/sarva-mark.png" },
  light: { lockup: "/brand/sarva-logo-white.png", mark: "/brand/sarva-mark-white.png" },
} as const;

export interface BrandProps {
  /** `mark` is the glyph alone — for the collapsed rail, where a wordmark cannot fit. */
  variant?: "lockup" | "mark";
  /** Rendered height in Tailwind's scale. The width follows from the aspect ratio. */
  className?: string;
  /**
   * Decorative where the name is already announced, or where the whole panel is
   * `aria-hidden`. Defaults to labelled, because the rail's lockup is the only
   * place the product names itself.
   */
  decorative?: boolean;
  /**
   * `dark` is the supplied artwork, for a light background. `light` is the
   * white knockout, for the brand gradient. Named for the *ink*, not the
   * ground, so `tone="light"` reads as "the light-coloured logo".
   */
  tone?: "dark" | "light";
}

export function Brand({
  variant = "lockup",
  className,
  decorative = false,
  tone = "dark",
}: BrandProps) {
  const isMark = variant === "mark";
  return (
    <img
      src={isMark ? ART[tone].mark : ART[tone].lockup}
      width={isMark ? 56 : 208}
      height={isMark ? 44 : 46}
      // A decorative image takes an empty alt AND `aria-hidden`; a meaningful one
      // takes the company name. "logo" is never in the alt — a screen reader
      // already says "image".
      {...(decorative ? { alt: "", "aria-hidden": true as const } : { alt: "Sarva Health" })}
      className={["w-auto select-none object-contain", className ?? ""].join(" ")}
      draggable={false}
    />
  );
}
